import { useState, useEffect, useMemo, useRef } from "react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Plus, Building2 } from "lucide-react"
import { useOrganisations, AdminOrganisation } from "@/hooks/use-admin-organisations"
import { OrganisationList } from "./organisation-list"
import { OrganisationForm } from "./organisation-form"
import { OrganisationDetail } from "./organisation-detail"
import { getPageHeader } from "@/constants"
import { PageContainer } from "@/components/ui/page-container"
import { PageHeader } from "@/components/ui/page-header"
import { PageHeaderLeading } from "@/components/navigation/page-header-leading"
import { PageHeaderStack } from "@/components/navigation/page-header-stack"
import {
  PageHeaderActionPrimary,
  PageHeaderActions,
} from "@/components/navigation/page-header-actions"
import { ListBrowseToolbar } from "@/components/filters/list-browse-toolbar"
import { type AdminBasePath } from '@/components/admin/use-admin-base-path'

interface AdminOrganisationsPageProps {
  basePath?: AdminBasePath
}

export function AdminOrganisationsPage({ basePath = '/platform' }: AdminOrganisationsPageProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<string | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)

  // Track previous organisations for agent update animation
  const [highlightedOrganisations, setHighlightedOrganisations] = useState<Set<string>>(new Set())
  const previousOrganisationsRef = useRef<AdminOrganisation[]>([])

  const { data: organisations, isLoading, refetch } = useOrganisations({
    limit: 50
  })

  // Detect agent updates and highlight changed organisations
  useEffect(() => {
    if (organisations && previousOrganisationsRef.current.length > 0) {
      const changedIds = new Set<string>()
      
      // Find new organisations
      organisations.forEach((org) => {
        const prev = previousOrganisationsRef.current.find((o) => o.id === org.id)
        if (!prev) {
          changedIds.add(org.id)
          console.log('[ORGANISATIONS] New organisation detected:', org.id, org.name)
        } else {
          // Check if organisation was updated (compare JSON strings)
          if (JSON.stringify(prev) !== JSON.stringify(org)) {
            changedIds.add(org.id)
            console.log('[ORGANISATIONS] Updated organisation detected:', org.id, org.name)
          }
        }
      })
      
      if (changedIds.size > 0) {
        console.log('[ORGANISATIONS] Highlighting organisations:', Array.from(changedIds))
        setHighlightedOrganisations(changedIds)
        // Fade out highlights after 3 seconds
        setTimeout(() => {
          setHighlightedOrganisations(new Set())
        }, 3000)
      }
    }
    
    // Update ref (doesn't trigger re-render)
    if (organisations) {
      previousOrganisationsRef.current = organisations
    }
  }, [organisations])

  const handleOrganisationClick = (organisationId: string) => {
    setSelectedOrganisationId(organisationId)
    setDetailDialogOpen(true)
  }

  const handleEditOrganisation = (organisationId: string) => {
    setSelectedOrganisationId(organisationId)
    setEditDialogOpen(true)
  }

  const handleCreateSuccess = () => {
    setCreateDialogOpen(false)
    refetch()
  }

  const handleEditSuccess = () => {
    setEditDialogOpen(false)
    setDetailDialogOpen(false)
    refetch()
  }

  const handleDetailClose = () => {
    setDetailDialogOpen(false)
    setSelectedOrganisationId(null)
  }

  const filteredOrganisations = useMemo(() => {
    const needle = searchQuery.trim().toLowerCase()
    if (!needle) return organisations || []
    return (organisations || []).filter((org) => org.name.toLowerCase().includes(needle))
  }, [organisations, searchQuery])

  return (
    <PageContainer>
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          {...getPageHeader(`${basePath}/organisations`, "Organisation management", "Manage companies and organisations in the system")}
          icon={<Building2 className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              primary={
                <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <PageHeaderActionPrimary>
                      <Plus className="h-4 w-4" aria-hidden />
                      Create Organisation
                    </PageHeaderActionPrimary>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Create new organisation</DialogTitle>
                      <DialogDescription>
                        Create a new organisation with details and industry information.
                      </DialogDescription>
                    </DialogHeader>
                    <OrganisationForm onSuccess={handleCreateSuccess} onCancel={() => setCreateDialogOpen(false)} />
                  </DialogContent>
                </Dialog>
              }
            />
          }
        />

        <ListBrowseToolbar
          searchId="admin-organisations-search"
          searchPlaceholder="Search organisations by name…"
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
        />
      </PageHeaderStack>

      <OrganisationList
        organisations={filteredOrganisations}
        isLoading={isLoading}
        onOrganisationClick={handleOrganisationClick}
        onEditOrganisation={handleEditOrganisation}
        highlightedOrganisationIds={highlightedOrganisations}
      />

      {/* Edit Organisation Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit organisation</DialogTitle>
            <DialogDescription>
              Update organisation information and details.
            </DialogDescription>
          </DialogHeader>
          {selectedOrganisationId && (
            <OrganisationForm
              organisationId={selectedOrganisationId}
              onSuccess={handleEditSuccess}
              onCancel={() => setEditDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Organisation Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={handleDetailClose}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Organisation details</DialogTitle>
            <DialogDescription>
              View complete organisation information and manage settings.
            </DialogDescription>
          </DialogHeader>
          {selectedOrganisationId && (
            <OrganisationDetail
              organisationId={selectedOrganisationId}
              onEdit={() => {
                setDetailDialogOpen(false)
                setEditDialogOpen(true)
              }}
              onClose={handleDetailClose}
            />
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}

